// Proof of Delivery Types
export interface DeliveryLocation {
  delivery_name: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  special_instructions?: string;
  access_requirements?: string;
  dock_information?: string;
}

export interface CarrierInfo {
  carrier_name?: string;
  tracking_number?: string;
  driver_name?: string;
  driver_contact?: string;
  vehicle_info?: string;
}

export interface DeliveryItem {
  item_description: string;
  quantity_ordered: number;
  quantity_delivered: number;
  unit_of_measure: string;
  condition_notes?: string;
  serial_numbers?: string[];
  lot_numbers?: string[];
}

export type DeliveryMethod = 'shipping' | 'pickup' | 'installation' | 'digital_delivery' | 'direct_handoff';
export type DeliveryCondition = 'excellent' | 'good' | 'fair' | 'damaged' | 'defective';
export type DeliveryPhotoType = 'before_delivery' | 'after_delivery' | 'installation' | 'packaging' | 'damage' | 'signature';

export interface DeliveryPhoto {
  id: string;
  document_id: string;
  photo_type: DeliveryPhotoType;
  caption?: string;
  timestamp: string;
  gps_coordinates?: string;
}

export interface DeliverySignature {
  signature_data: string; // Base64 encoded signature
  recipient_name: string;
  recipient_title?: string;
  recipient_id_verified: boolean;
  signature_timestamp: string;
  device_info?: string;
}

export interface DeliveryProof {
  id: string;
  public_id: string;
  job_id: string;
  organization_id: string;
  purchase_order_id?: string;
  
  // Delivery basics
  delivery_date: string;
  delivery_time?: string;
  delivery_method: DeliveryMethod;
  delivery_location: any; // JSON field
  
  // Carrier and tracking
  carrier_info?: any; // JSON field
  
  // Items delivered
  items_delivered: any[]; // JSON field
  partial_delivery: boolean;
  
  // Condition and quality
  condition_on_delivery: DeliveryCondition;
  delivery_notes?: string;
  exceptions_noted?: string;
  
  // Documentation
  delivery_photos: any[]; // JSON field
  delivery_receipts: string[]; // Document IDs
  packing_slip_url?: string;
  invoice_reference?: string;
  
  // Signatures
  recipient_signature?: any; // JSON field
  driver_signature?: any; // JSON field
  
  // Installation specific (if applicable)
  installation_notes?: string;
  installation_photos?: string[]; // Document IDs
  warranty_activation?: boolean;
  training_provided?: boolean;
  
  // System fields
  submitted_by_user_id: string;
  created_at: string;
  updated_at: string;
  
  // Client review
  client_review_status: 'pending' | 'accepted' | 'rejected' | 'needs_clarification';
  client_reviewed_at?: string;
  client_reviewed_by_user_id?: string;
  client_review_notes?: string;
  auto_accept_at: string;
  
  // Resubmission handling
  resubmission_count: number;
  max_resubmissions: number;
  
  // Required field for database compatibility
  recipient_name: string;
}

export interface DeliveryException {
  id: string;
  delivery_proof_id: string;
  exception_type: 'damaged_goods' | 'missing_items' | 'wrong_items' | 'late_delivery' | 'access_issues' | 'other';
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  photos: string[]; // Document IDs
  resolution_required: boolean;
  resolved_at?: string;
  resolved_by_user_id?: string;
  resolution_notes?: string;
  created_by_user_id: string;
  created_at: string;
}

// Delivery workflow constants
export const DELIVERY_METHODS: { value: DeliveryMethod; label: string }[] = [
  { value: 'shipping', label: 'Shipping/Freight' },
  { value: 'pickup', label: 'Customer Pickup' },
  { value: 'installation', label: 'Delivery & Installation' },
  { value: 'digital_delivery', label: 'Digital Delivery' },
  { value: 'direct_handoff', label: 'Direct Handoff' },
];

export const DELIVERY_CONDITIONS: { value: DeliveryCondition; label: string }[] = [
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'defective', label: 'Defective' },
];

export const DELIVERY_PHOTO_TYPES: { value: DeliveryPhotoType; label: string }[] = [
  { value: 'before_delivery', label: 'Before Delivery' },
  { value: 'after_delivery', label: 'After Delivery' },
  { value: 'installation', label: 'Installation Process' },
  { value: 'packaging', label: 'Packaging' },
  { value: 'damage', label: 'Damage Documentation' },
  { value: 'signature', label: 'Signature Capture' },
];

export const EXCEPTION_TYPES: { value: string; label: string }[] = [
  { value: 'damaged_goods', label: 'Damaged Goods' },
  { value: 'missing_items', label: 'Missing Items' },
  { value: 'wrong_items', label: 'Wrong Items' },
  { value: 'late_delivery', label: 'Late Delivery' },
  { value: 'access_issues', label: 'Access Issues' },
  { value: 'other', label: 'Other' },
];

export const EXCEPTION_SEVERITIES: { value: string; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

// Form validation constants
export const MIN_DELIVERY_PHOTOS = 2;
export const REQUIRED_PHOTO_TYPES = ['before_delivery', 'after_delivery'];
export const MAX_DELIVERY_PHOTOS = 20;
export const SIGNATURE_REQUIRED_METHODS: DeliveryMethod[] = ['shipping', 'pickup', 'installation', 'direct_handoff'];